import { useEffect, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ListingCard from "@/components/ListingCard";
import { supabase } from "@/integrations/supabase/client";

const speciesOptions = ["Toutes", "Chêne", "Hêtre", "Frêne", "Mélange"];
const seasoningOptions = ["Tous", "Sec (>2 ans)", "Demi-sec (1 an)", "Vert (frais)"];
const lengthOptions = ["Toutes", "25cm", "33cm", "50cm", "1m"];

const Listings = () => {
  useDocumentTitle(
    "Annonces bois de chauffage à Chimay, Couvin, Momignies | ChimayBois",
    "Parcourez toutes les annonces de bois de chauffage dans la région de Chimay. Chêne, hêtre, frêne — vendeurs vérifiés, prix transparents."
  );
  const [search, setSearch] = useState("");
  const [species, setSpecies] = useState("Toutes");
  const [seasoning, setSeasoning] = useState("Tous");
  const [length, setLength] = useState("Toutes");
  const [showFilters, setShowFilters] = useState(false);
  const [listings, setListings] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const fetchListings = async () => {
      const { data: listingsData } = await supabase
        .from("listings")
        .select("*")
        .in("status", ["active", "sold_out"])
        .order("created_at", { ascending: false });

      if (listingsData && listingsData.length > 0) {
        const sellerIds = [...new Set(listingsData.map((l) => l.seller_id))];
        const [{ data: sellers }, { data: reviewsData }] = await Promise.all([
          supabase.from("seller_profiles_public").select("*").in("user_id", sellerIds),
          supabase.from("reviews").select("seller_id, rating").in("seller_id", sellerIds),
        ]);
        const sellerMap = Object.fromEntries((sellers ?? []).map((s) => [s.user_id, s]));

        // Compute average review rating per seller
        const ratingMap: Record<string, { sum: number; count: number }> = {};
        (reviewsData ?? []).forEach((r: any) => {
          if (!ratingMap[r.seller_id]) ratingMap[r.seller_id] = { sum: 0, count: 0 };
          ratingMap[r.seller_id].sum += r.rating;
          ratingMap[r.seller_id].count += 1;
        });

        setListings(listingsData.map((l) => {
          const seller = sellerMap[l.seller_id] || null;
          const avg = ratingMap[l.seller_id] ? ratingMap[l.seller_id].sum / ratingMap[l.seller_id].count : 0;
          const reviewCount = ratingMap[l.seller_id]?.count ?? 0;
          return { ...l, seller, rating: avg, reviewCount };
        }));
      } else {
        setListings([]);
      }
      setLoadingData(false);
    };
    fetchListings();
  }, []);

  const filtered = listings.filter((l) => {
    if (search && !l.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (species !== "Toutes" && l.species !== species) return false;
    if (seasoning !== "Tous" && l.seasoning !== seasoning) return false;
    if (length !== "Toutes" && l.length !== length) return false;
    return true;
  });

  const hasActiveFilters = species !== "Toutes" || seasoning !== "Tous" || length !== "Toutes";

  const clearFilters = () => {
    setSpecies("Toutes");
    setSeasoning("Tous");
    setLength("Toutes");
    setSearch("");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="container py-8 flex-1">
        <h1 className="font-display text-3xl font-bold mb-6">Bois de chauffage à vendre — Chimay et alentours</h1>

        {/* Search + Filter toggle */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 flex items-center gap-2 bg-card rounded-lg px-4 border">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par essence, titre..."
              className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span className="hidden sm:inline">Filtres</span>
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-card rounded-lg border p-4 mb-6 animate-fade-in grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Essence</label>
              <Select value={species} onValueChange={setSpecies}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {speciesOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Séchage</label>
              <Select value={seasoning} onValueChange={setSeasoning}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {seasoningOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Longueur</label>
              <Select value={length} onValueChange={setLength}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {lengthOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {hasActiveFilters && (
          <button onClick={clearFilters} className="text-xs text-primary flex items-center gap-1 mb-4 hover:underline">
            <X className="h-3 w-3" /> Effacer les filtres
          </button>
        )}

        {/* Results */}
        <p className="text-sm text-muted-foreground mb-4">{filtered.length} annonce{filtered.length !== 1 ? "s" : ""} trouvée{filtered.length !== 1 ? "s" : ""}</p>

        {loadingData ? (
          <div className="text-center py-20 text-muted-foreground">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="text-lg font-display">Aucune annonce trouvée</p>
            <p className="text-sm mt-2">Essayez de modifier vos filtres ou revenez bientôt.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Listings;
